import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flag, Search, ChevronLeft, ChevronRight, Wand2, Upload } from "lucide-react";
import { getVzGoals, applyVzGoalDefaults, uploadVzGoals, getVzDealerSummary, getVzAdminSummary } from "@/lib/verizonSalesApi";
import { buildPerStore } from "./DealerSalesCallouts";
import { GOAL_COLS, GoalCell } from "./GoalCell";
import { QuotaSettingsPanel } from "./QuotaSettingsPanel";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const shift = (m, d) => { const dt = new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + d, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`; };
const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Save failed"; };

const AdminGoalTools = ({ month, onDone }) => {
  const [pct, setPct] = useState(10);
  const fileRef = useRef(null);
  const apply = async () => {
    if (!window.confirm(`Set every store's minimum activation goal to its Verizon quota and upgrade goal to ${pct}% of it for this month?`)) return;
    try { const r = await applyVzGoalDefaults(month, pct / 100); toast.success(`Defaults applied to ${r.updated} stores`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    try {
      const r = await uploadVzGoals(month, file);
      toast.success(`Goals loaded for ${r.updated} ${r.updated === 1 ? "store" : "stores"}${r.unknown.length ? ` · ${r.unknown.length} not found: ${r.unknown.slice(0, 5).join(", ")}` : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap px-4 pb-3" data-testid="vz-goal-admin-tools">
      <span className="text-xs text-slate-500">Upgrade goal =</span>
      <Input type="number" min={0} max={500} value={pct} onChange={(e) => setPct(Number(e.target.value))} className="h-8 w-16 text-xs" data-testid="vz-goal-upgrade-pct-input" />
      <span className="text-xs text-slate-500">% of activation goal</span>
      <Button size="sm" variant="outline" onClick={apply} data-testid="vz-goal-apply-defaults-btn"><Wand2 className="w-4 h-4 mr-1" /> Apply defaults (quota + {pct}%)</Button>
      <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="vz-goal-upload-input" />
      <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="vz-goal-upload-btn"><Upload className="w-4 h-4 mr-1" /> Upload goals (CSV/Excel)</Button>
      <span className="text-[11px] text-slate-400">Columns: Store ID (or TSP), Activation Goal, Upgrade Goal</span>
    </div>
  );
};

export const StoreGoalsCard = ({ mode, onChanged }) => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [current, setCurrent] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(null);
  useEffect(() => { (mode === "admin" ? getVzAdminSummary() : getVzDealerSummary()).then(setLive).catch(() => {}); }, [mode, data]);
  const cards = useMemo(() => (live ? Object.fromEntries(buildPerStore(live.stores, { summaryTotals: live.totals, qs: live.quota_settings, mr: true })
    .map((x) => [x.store.door_tsp, x.cards])) : {}), [live]);
  const load = useCallback((m) => {
    setLoading(true);
    return getVzGoals(mode, m).then((d) => { setData(d); setMonth(d.month); setCurrent((c) => c || d.month); })
      .catch(() => toast.error("Failed to load goals")).finally(() => setLoading(false));
  }, [mode]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data?.stores || []).filter((s) => !q || [s.label, s.store_id, s.city, s.sub_agent_name].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase()))), [data, q]);
  if (!data) return null;
  const isCurrent = month === current;
  const saved = () => { load(month); onChanged?.(); };

  return (
    <Card className="border-slate-200" data-testid="vz-goals-card">
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Flag className="w-4 h-4 text-indigo-500" /> Monthly Store Goals</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">{mode === "admin"
            ? "Every store's goals for each card. Only Prime Media admins set or upload goals."
            : "Prime Media sets your minimum goals. You can raise them but not go below the minimum."} A new goal is set each month.</p>
        </div>
        <div className="flex items-center gap-2">
          {mode === "admin" && (
            <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-8 pl-8 w-44 text-xs" data-testid="vz-goals-search" /></div>
          )}
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={loading} onClick={() => load(shift(month, -1))} data-testid="vz-goals-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium text-navy w-32 text-center" data-testid="vz-goals-month">{MONTHS[Number(month.slice(5, 7)) - 1]} {month.slice(0, 4)}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={loading} onClick={() => load(shift(month, 1))} data-testid="vz-goals-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      {mode === "admin" && <AdminGoalTools month={month} onDone={saved} />}
      {mode === "admin" && <QuotaSettingsPanel stores={live?.stores || []} onSaved={saved} />}
      <CardContent className="px-0 overflow-hidden">
        <table className="vz-compact w-full" data-testid="vz-goals-table">
          <thead><tr className="text-slate-500 border-b border-slate-200">
            <th className="px-4 py-2 text-left sticky left-0 bg-white">Store ID</th>
            {GOAL_COLS.map((c) => <th key={c.goal} className="px-1.5 py-2 text-center border-l border-slate-100 whitespace-nowrap">{c.label}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.door_tsp} className="border-b border-slate-100" data-testid={`vz-goal-row-${g.door_tsp}`}>
                <td className="px-4 py-1.5 font-medium text-navy sticky left-0 bg-white" title={`${g.label} · ${g.address || ""}`}>{g.store_id}</td>
                {GOAL_COLS.map((c) => (
                  <GoalCell key={c.goal} col={c} card={isCurrent ? cards[g.door_tsp]?.[c.card] : null} g={g} mode={mode} month={month}
                    editable={isCurrent && mode === "admin"} onSaved={saved} />
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={GOAL_COLS.length + 1} className="text-center text-slate-500 py-6">No stores.</td></tr>}
          </tbody>
        </table>
        <p className="px-4 pt-2 text-[11px] text-slate-400">Each cell shows the store's goal with its current value underneath (green = on track, red = behind). {mode === "admin"
          ? "Activation and Upgrade goals are set here (or by upload); other goals come from the quota bar above."
          : "Goals start at Prime Media's goal; you can raise any goal but not go below it."}</p>
      </CardContent>
    </Card>
  );
};
